import { makeAutoObservable } from 'mobx';
import type { Item, ItemAvailability } from 'root/types/item';
import type { TFunction } from '@wix/yoshi-flow-editor';
import type { AvailabilityStatusKeysConfig } from 'root/availabilityStatusKeys';
import type { TimeSlot, DispatchInfo } from 'root/types/businessTypes';
import type { CommonAvailability } from 'root/types/availability';
import { AvailabilityState } from './AvailabilityState';
import type { MenuState } from './MenuState';
import type { SectionState } from './SectionState';
import type { AvailabilityStatus } from '@wix/ambassador-restaurants-menu-settings-v1-menu-ordering-settings/types';

export class ItemState {
  item: Item;
  private availabilityState: AvailabilityState;

  constructor(item: Item) {
    this.item = item;
    this.availabilityState = new AvailabilityState(
      item.orderSettings?.availability as CommonAvailability
    );
    makeAutoObservable(this);
  }

  get id() {
    return this.item.id;
  }

  get isAvailable() {
    return this.availabilityState.isAvailable;
  }

  get availability(): CommonAvailability | undefined {
    return this.availabilityState.currentAvailability;
  }

  get hasNextAvailableTimeslot() {
    return this.availabilityState.hasNextAvailableTimeslot;
  }

  updateItemAvailability(itemAvailability: ItemAvailability) {
    this.availabilityState.updateAvailability(itemAvailability as CommonAvailability);
  }

  updateAvailabilityStatus(availabilityStatus: AvailabilityStatus) {
    this.availabilityState.updateAvailabilityStatus(availabilityStatus);
  }

  initializeAvailability(
    selectedTimeSlot?: TimeSlot,
    timezone?: string,
    businessDaysAhead?: number
  ) {
    this.availabilityState.initializeAvailability(selectedTimeSlot, timezone, businessDaysAhead);
  }

  updateAvailabilityForDispatch(dispatchInfo: DispatchInfo) {
    this.availabilityState.updateAvailabilityForDispatch(dispatchInfo);
  }

  getAvailabilityStatus({
    locale,
    timezone,
    t,
    keys,
  }: {
    locale: string;
    timezone: string;
    t: TFunction;
    keys: AvailabilityStatusKeysConfig;
  }) {
    return this.availabilityState.getAvailabilityStatus({
      locale,
      timezone,
      t,
      keys,
    });
  }

  getEffectiveAvailability({
    menuState,
    sectionState,
    enableSectionItemAvailability,
  }: {
    menuState?: MenuState;
    sectionState?: SectionState;
    enableSectionItemAvailability: boolean;
  }) {
    const isMenuAvailable = menuState?.isAvailable ?? true;
    const menuHasNextAvailableTime = !!menuState?.hasNextAvailableTimeslot;

    const isSectionAvailable = enableSectionItemAvailability
      ? sectionState?.isAvailable ?? true
      : true;
    const sectionHasNextAvailableTime = enableSectionItemAvailability
      ? !!sectionState?.hasNextAvailableTimeslot
      : false;

    const isItemAvailable = enableSectionItemAvailability ? this.isAvailable : true;
    const itemHasNextAvailableTime = enableSectionItemAvailability
      ? this.hasNextAvailableTimeslot
      : false;

    const isUnavailable =
      (!isMenuAvailable && !menuHasNextAvailableTime) ||
      (enableSectionItemAvailability && !isSectionAvailable && !sectionHasNextAvailableTime) ||
      (enableSectionItemAvailability && !isItemAvailable && !itemHasNextAvailableTime);

    if (isUnavailable) {
      return {
        isUnavailable: true,
        isAvailableForFutureOrder: false,
        isAvailable: false,
      };
    }

    const isAvailableForFutureOrder =
      enableSectionItemAvailability &&
      ((!isMenuAvailable && menuHasNextAvailableTime) ||
        sectionHasNextAvailableTime ||
        itemHasNextAvailableTime);

    if (isAvailableForFutureOrder) {
      return {
        isUnavailable: false,
        isAvailableForFutureOrder: true,
        isAvailable: false,
      };
    }

    return {
      isUnavailable: false,
      isAvailableForFutureOrder: false,
      isAvailable: true,
    };
  }
}
